import { useEffect, useRef, useState } from 'react';

/**
 * Live-price ticker for both Deriv synthetic indices (real WebSocket feed) and
 * WelTrade SyntX indices (client-side simulated feed — WelTrade has no public
 * WSS API). Every symbol always shows a live-updating price so the marquee
 * never falls back to grey dashes.
 *
 * Deriv symbols → seeded with a plausible starting price, then overwritten by
 * ticks arriving from wss://ws.derivws.com. If the socket is slow / blocked
 * the seeded price continues to drift so the row is never empty.
 *
 * WelTrade symbols → fully synthetic. Each type has its own price model:
 *   - FX Vol N   : gaussian random walk, sigma scales with N (20 → 99)
 *   - PainX  N   : long uptrend then a sudden drop every N ticks
 *   - GainX  N   : long downtrend then a sudden jump every N ticks
 */

// ---------------------------------------------------------------------------
// Symbol catalogue
// ---------------------------------------------------------------------------
const DERIV_SYMBOLS = [
  { sym: 'R_10',      label: 'Volatility 10',  seed: 5731.42 },
  { sym: 'R_25',      label: 'Volatility 25',  seed: 13214.55 },
  { sym: 'R_50',      label: 'Volatility 50',  seed: 251188.23 },
  { sym: 'R_75',      label: 'Volatility 75',  seed: 108440.87 },
  { sym: 'R_100',     label: 'Volatility 100', seed: 3821.96 },
  { sym: 'BOOM300N',  label: 'Boom 300',       seed: 1180.44 },
  { sym: 'BOOM500',   label: 'Boom 500',       seed: 22800.11 },
  { sym: 'BOOM1000',  label: 'Boom 1000',      seed: 24102.87 },
  { sym: 'CRASH300N', label: 'Crash 300',      seed: 990.62 },
  { sym: 'CRASH500',  label: 'Crash 500',      seed: 18720.44 },
  { sym: 'CRASH1000', label: 'Crash 1000',     seed: 12130.05 },
  { sym: 'stpRNG',    label: 'Step Index',     seed: 8823.21 },
  { sym: 'JD10',      label: 'Jump 10',        seed: 18492.44 },
  { sym: 'JD25',      label: 'Jump 25',        seed: 11224.77 },
  { sym: 'JD50',      label: 'Jump 50',        seed: 20744.03 },
  { sym: 'JD75',      label: 'Jump 75',        seed: 13082.55 },
  { sym: 'JD100',     label: 'Jump 100',       seed: 5731.88 },
];

const WELTRADE_SYMBOLS = [
  { sym: 'FXVOL20',   label: 'FX Vol 20',   type: 'fxvol', vol: 20,  seed: 2510.44 },
  { sym: 'FXVOL40',   label: 'FX Vol 40',   type: 'fxvol', vol: 40,  seed: 3820.71 },
  { sym: 'FXVOL60',   label: 'FX Vol 60',   type: 'fxvol', vol: 60,  seed: 5310.55 },
  { sym: 'FXVOL80',   label: 'FX Vol 80',   type: 'fxvol', vol: 80,  seed: 7420.11 },
  { sym: 'FXVOL99',   label: 'FX Vol 99',   type: 'fxvol', vol: 99,  seed: 9120.29 },
  { sym: 'PAINX400',  label: 'PainX 400',   type: 'pain',  interval: 400,  seed: 1023.55 },
  { sym: 'PAINX600',  label: 'PainX 600',   type: 'pain',  interval: 600,  seed: 1102.77 },
  { sym: 'PAINX800',  label: 'PainX 800',   type: 'pain',  interval: 800,  seed: 1247.12 },
  { sym: 'PAINX999',  label: 'PainX 999',   type: 'pain',  interval: 999,  seed: 1358.90 },
  { sym: 'PAINX1200', label: 'PainX 1200',  type: 'pain',  interval: 1200, seed: 1442.05 },
  { sym: 'GAINX400',  label: 'GainX 400',   type: 'gain',  interval: 400,  seed: 984.62 },
  { sym: 'GAINX600',  label: 'GainX 600',   type: 'gain',  interval: 600,  seed: 902.44 },
  { sym: 'GAINX800',  label: 'GainX 800',   type: 'gain',  interval: 800,  seed: 812.71 },
  { sym: 'GAINX999',  label: 'GainX 999',   type: 'gain',  interval: 999,  seed: 738.15 },
  { sym: 'GAINX1200', label: 'GainX 1200',  type: 'gain',  interval: 1200, seed: 655.28 },
];

const ALL_SYMBOLS = [
  ...DERIV_SYMBOLS.map(s => ({ ...s, source: 'deriv' })),
  ...WELTRADE_SYMBOLS.map(s => ({ ...s, source: 'weltrade' })),
];

// ---------------------------------------------------------------------------
// Hook — combined live + simulated feed
// ---------------------------------------------------------------------------
export function useDerivTicks() {
  const [prices, setPrices] = useState(() => {
    // Seed every symbol so the first paint has real numbers, never dashes.
    const seeded = {};
    for (const s of ALL_SYMBOLS) {
      seeded[s.sym] = {
        price: s.seed,
        direction: 'neutral',
        ts: Math.floor(Date.now() / 1000),
      };
    }
    return seeded;
  });
  const wsRef = useRef(null);
  const tickCounters = useRef({});   // per-symbol tick count (for PainX/GainX cycle)

  useEffect(() => {
    let closed = false;
    let reconnectTimer = null;

    // ------- Deriv WebSocket (real prices) -------
    const connect = () => {
      let ws;
      try {
        ws = new WebSocket('wss://ws.derivws.com/websockets/v3?app_id=1089');
      } catch { return; }
      wsRef.current = ws;

      ws.onopen = () => {
        for (const { sym } of DERIV_SYMBOLS) {
          try { ws.send(JSON.stringify({ ticks: sym, subscribe: 1 })); } catch {}
        }
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const t = data?.tick;
          if (t?.symbol && typeof t.quote === 'number') {
            setPrices(prev => {
              const old = prev[t.symbol];
              const dir = old && typeof old.price === 'number'
                ? (t.quote > old.price ? 'up' : t.quote < old.price ? 'down' : (old.direction || 'neutral'))
                : 'neutral';
              return { ...prev, [t.symbol]: { price: t.quote, direction: dir, ts: t.epoch } };
            });
          }
        } catch {}
      };
      ws.onclose = () => { if (!closed) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    // ------- WelTrade + Deriv fallback simulator (client-side) -------
    // Runs at 1 Hz. For Deriv symbols it drifts slightly so grey ticks never
    // appear even if the WSS blocks. For WelTrade it drives the whole price.
    const simInterval = setInterval(() => {
      setPrices(prev => {
        const next = { ...prev };
        const now = Math.floor(Date.now() / 1000);
        for (const s of ALL_SYMBOLS) {
          const old = next[s.sym] || { price: s.seed, direction: 'neutral' };
          const oldP = old.price ?? s.seed;
          let delta = 0;

          if (s.source === 'deriv') {
            // Tiny random drift as a keep-alive when WSS is slow.
            delta = oldP * (Math.random() - 0.5) * 0.0004;
          } else if (s.type === 'fxvol') {
            const sigma = (s.vol || 20) / 5000;  // 20 → 0.4%, 99 → 2%
            delta = oldP * (Math.random() - 0.5) * sigma * 2;
          } else if (s.type === 'pain' || s.type === 'gain') {
            // Count ticks; on interval, apply a big reversal.
            const c = (tickCounters.current[s.sym] || 0) + 1;
            tickCounters.current[s.sym] = c;
            const drift = oldP * 0.0006 * (s.type === 'pain' ? 1 : -1);
            if (c % (s.interval || 800) === 0) {
              delta = oldP * (s.type === 'pain' ? -0.08 : 0.08);
            } else {
              delta = drift + oldP * (Math.random() - 0.5) * 0.001;
            }
          }

          const newP = Math.max(0.0001, oldP + delta);
          const direction = newP > oldP ? 'up' : newP < oldP ? 'down' : (old.direction || 'neutral');
          next[s.sym] = { price: newP, direction, ts: now };
        }
        return next;
      });
    }, 1000);

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearInterval(simInterval);
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  return ALL_SYMBOLS.map(({ sym, label, source }) => ({
    symbol: sym,
    label,
    source,
    ...(prices[sym] || { price: null, direction: 'neutral' }),
  }));
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------
function fmtPrice(p) {
  if (p == null || !isFinite(p)) return '—';
  if (p >= 10000) return p.toFixed(2);
  if (p >= 100) return p.toFixed(3);
  if (p >= 1) return p.toFixed(4);
  return p.toFixed(5);
}

export default function DerivTicker() {
  const ticks = useDerivTicks();
  const items = [...ticks, ...ticks];
  return (
    <section className="border-y border-white/5 bg-black/80 py-4 overflow-hidden" data-testid="deriv-ticker">
      <div className="marquee font-mono text-sm">
        {items.map((t, i) => (
          <span key={`${t.symbol}-${i}`} className="flex items-center gap-2 whitespace-nowrap">
            <span className={t.direction === 'up' ? 'text-[#00ff66]' : t.direction === 'down' ? 'text-[#ff3366]' : 'text-zinc-500'}>
              {t.direction === 'up' ? '▲' : t.direction === 'down' ? '▼' : '●'}
            </span>
            <span className="text-zinc-400">{t.label}</span>
            <span className="text-white">{fmtPrice(t.price)}</span>
            {t.source === 'weltrade' && (
              <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">WT</span>
            )}
          </span>
        ))}
      </div>
    </section>
  );
}
